import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { IoAddOutline } from "react-icons/io5";
import { HiOutlineSearch, HiStar } from "react-icons/hi";
import { TiStarOutline } from "react-icons/ti";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdClear, MdDriveFileRenameOutline } from "react-icons/md";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";
import EventStatusBadge from "./EventStatusBadge";
import styles from "./style/EventHighlightsList.module.scss";
import {
  getHighlightPublishMissingFields,
  highlightListRowToPublishCheckForm,
  normalizeMetaJson,
} from "./activityTransforms";
import {
  deleteActivityHighlight,
  fetchActivityHighlightsList,
  pinActivityHighlight,
  updateActivityHighlight,
} from "./activitiesApi";

const LIMIT = 10;
const EMPTY_DISPLAY = "未設定";

const EVENT_TYPE_META_TO_LABEL = {
  sharing: "分享會",
  launch: "發表會",
  workshop: "工作坊",
};

const toValidDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const mapHighlightRow = (row) => {
  const meta = normalizeMetaJson(row.meta_json);
  const eventTypeKey =
    typeof meta.event_type === "string" ? meta.event_type : "";
  const typeLabel = EVENT_TYPE_META_TO_LABEL[eventTypeKey] ?? "";
  return {
    id: row.id,
    name: row.title ?? "",
    isPinned: Boolean(row.is_pinned),
    isPublished: Boolean(row.is_published),
    type: typeLabel || EMPTY_DISPLAY,
    eventTypeForPublish: typeLabel,
    description: typeof row.description === "string" ? row.description : "",
    publishedAt: row.published_at ?? null,
  };
};

const EventHighlightsList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [publishBusyId, setPublishBusyId] = useState(null);
  const [pinBusyId, setPinBusyId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await fetchActivityHighlightsList({
        search: searchApplied,
        page,
        limit: LIMIT,
      });
      const apiRows = data?.rows ?? [];
      setRows(apiRows.map(mapHighlightRow));
      setTotal(Number(data?.total) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, searchApplied]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const handlePublishToggle = async (item, nextChecked) => {
    if (publishBusyId === item.id) return;
    if (nextChecked) {
      const missing = getHighlightPublishMissingFields(
        highlightListRowToPublishCheckForm(item),
      );
      if (missing.length > 0) {
        showToast("尚有未填寫欄位", true);
        return;
      }
    }
    setPublishBusyId(item.id);
    try {
      await updateActivityHighlight(item.id, { is_published: nextChecked });
      setRows((prev) =>
        prev.map((r) =>
          r.id === item.id ? { ...r, isPublished: nextChecked } : r,
        ),
      );
      if (nextChecked) {
        const label = item.name?.trim() || "未命名花絮";
        showToast(`已成功上架活動花絮「${label}」。`, false);
      }
    } catch {
      showToast("儲存失敗請重試", true);
    } finally {
      setPublishBusyId(null);
    }
  };

  const handlePinToggle = async (item) => {
    if (!item.isPublished || pinBusyId === item.id || item.isPinned) return;
    setPinBusyId(item.id);
    try {
      await pinActivityHighlight(item.id);
      setRows((prev) =>
        prev.map((r) => ({ ...r, isPinned: r.id === item.id })),
      );
    } catch {
      showToast("儲存失敗請重試", true);
    } finally {
      setPinBusyId(null);
    }
  };

  return (
    <RightPanelLayout title="活動花絮">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>活動花絮列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={searchInput}
              placeholder="搜尋花絮名稱"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label="搜尋花絮名稱"
            />
            {searchInput ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applyListSearch} />
          <Button
            type="primary"
            title="新增"
            icon={<IoAddOutline fontSize={20} />}
            onClick={() => navigate("/dashboard/tdp/events/highlights/new")}
          />
        </div>
      </div>

      {!listLoading && total > 0 && (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>活動花絮名稱</th>
              <th>狀態</th>
              <th>置頂封面</th>
              <th>上架狀態</th>
              <th>類型</th>
              <th>上架時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={7} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}
            {!listLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.tableEmptyCell}>
                  尚無花絮資料
                </td>
              </tr>
            ) : null}
            {!listLoading
              ? rows.map((item) => {
                  const publishedDate = toValidDate(item.publishedAt);
                  const pinDisabled = !item.isPublished || pinBusyId === item.id;
                  return (
                    <tr key={item.id}>
                      <td>{item.name || EMPTY_DISPLAY}</td>
                      <td>
                        <EventStatusBadge
                          theme={item.isPublished ? "published" : "unpublished"}
                          text={item.isPublished ? "已上架" : "未上架"}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className={
                            pinDisabled
                              ? styles.pinStarButtonDisabled
                              : styles.pinStarButton
                          }
                          disabled={pinDisabled}
                          onClick={() => handlePinToggle(item)}
                          aria-label={item.isPinned ? "已置頂" : "置頂"}
                        >
                          {item.isPublished && item.isPinned ? (
                            <HiStar size={22} aria-hidden />
                          ) : (
                            <TiStarOutline size={22} aria-hidden />
                          )}
                        </button>
                      </td>
                      <td>
                        <div className={styles.switchWrap}>
                          <Switcher
                            isChecked={item.isPublished}
                            disabled={publishBusyId === item.id}
                            onChange={(e) => {
                              handlePublishToggle(item, e.target.checked);
                            }}
                            color={{ on: "#5856D6", off: "#5856D64D" }}
                            width={33}
                            height={20}
                          />
                        </div>
                      </td>
                      <td>
                        <EventStatusBadge theme="open" text={item.type} />
                      </td>
                      <td>
                        {publishedDate
                          ? format(publishedDate, "yyyy-MM-dd")
                          : EMPTY_DISPLAY}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.editButton}
                            onClick={() =>
                              navigate(
                                `/dashboard/tdp/events/highlights/${item.id}/edit`,
                              )
                            }
                            aria-label="編輯花絮"
                          >
                            <MdDriveFileRenameOutline />
                          </button>
                          <button
                            type="button"
                            className={styles.deleteButton}
                            onClick={() => setDeleteTargetId(item.id)}
                            aria-label="刪除花絮"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      <Alert
        isOpen={deleteTargetId != null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={async () => {
          if (deleteTargetId == null) return;
          const id = deleteTargetId;
          try {
            await deleteActivityHighlight(id);
            setDeleteTargetId(null);
            showToast("已刪除活動花絮", false);
            await loadList();
          } catch {
            showToast("刪除失敗請重試", true);
          }
        }}
        leftButtonText="取消"
        rightButtonText="刪除花絮"
        mainTitle="確認刪除花絮？"
        subTitle="刪除後無法復原。"
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default EventHighlightsList;
